import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAssignmentProgress } from '../hooks/useAssignmentProgress';
import { useDispatchPreview } from '../hooks/useDispatchPreview';
import { useEnRouteSummary } from '../hooks/useEnRouteSummary';
import { useExceptionOrCancelled } from '../hooks/useExceptionOrCancelled';
import { STAGE_LABEL } from './BatchBoard';
import type { BoardDay } from './boardModel';
import type { DeliveryFlowWire } from './boardWire';
import type { RouteEtaSummary } from './etaSignals';
import { EXECUTOR_LABEL, flowGateTime, flowProvider } from './flowCopy';
import { flowStepModel } from './flowSteps';
import {
  capFocusCards,
  hasActionableToday,
  holdFocusCards,
  type FocusCard,
  type FocusFormat,
} from './focusCards';
import { formatBatchDay, formatMonthDay } from './formatBatchDay';

interface UseFocusCardsArgs {
  /** The real today; every read below is for this date, never the selected one. */
  today: string;
  /** The board window rows. */
  days: readonly BoardDay[];
  /** The page's flow read: the cards need its answer, and say so when it failed. */
  flow: { data: DeliveryFlowWire | undefined; isError: boolean };
  /** Today's ETA summary the page already holds; the cards open no ETA query. */
  eta: RouteEtaSummary | undefined;
  nowMs: number;
  /** False while the cards are not rendered (the map view): no request then. */
  enabled: boolean;
}

export interface FocusCardsResult {
  cards: FocusCard[];
  /**
   * Every card that holds, before the cap. The phone headline reads its
   * progress card here (#2487 N1), so the desktop cap never drops it.
   */
  held: FocusCard[];
  /**
   * True only when no card holds, every read the cards need has answered, and
   * today's board counts show nothing to act on (`hasActionableToday`).
   */
  allClear: boolean;
  /** A read the cards need failed with nothing cached. */
  failed: boolean;
}

/**
 * Today's "needs attention now" cards (#2485). Each detail read is requested
 * only when today's board counts say it has something to tell, through the
 * same hook (and so the same query key) as its detail pane. A read that is not
 * enabled contributes nothing, even if an older answer sits in the cache.
 */
export function useFocusCards({
  today,
  days,
  flow: flowRead,
  eta,
  nowMs,
  enabled,
}: UseFocusCardsArgs): FocusCardsResult {
  const { t, i18n } = useTranslation();
  const todayRow = days.find((day) => day.date === today);
  const nextBatch = days
    .filter((day) => day.date > today && day.counts.awaiting_dispatch > 0)
    .reduce<BoardDay | undefined>(
      (earliest, day) => (earliest && earliest.date < day.date ? earliest : day),
      undefined,
    );

  const wantPreview = enabled && (todayRow?.signals.preflightAnomalies ?? 0) > 0;
  const wantAssignment = enabled && (todayRow?.counts.awaiting_assignment ?? 0) > 0;
  const wantExceptions = enabled && (todayRow?.counts.exception_or_cancelled ?? 0) > 0;
  const wantEnRoute = enabled && (todayRow?.counts.en_route ?? 0) > 0;
  const preview = useDispatchPreview(today, { enabled: wantPreview });
  const assignment = useAssignmentProgress(today, { enabled: wantAssignment });
  const exceptions = useExceptionOrCancelled(today, { enabled: wantExceptions });
  const enRoute = useEnRouteSummary(today, true, { enabled: wantEnRoute });

  const previewData = wantPreview ? preview.data : undefined;
  const assignmentData = wantAssignment ? assignment.data : undefined;
  const exceptionData = wantExceptions ? exceptions.data : undefined;
  // A group with neither a local driver nor an Onfleet worker is not a driver.
  const enRouteDriverCount = wantEnRoute
    ? enRoute.data?.drivers.filter((d) => d.driver_id !== null || d.onfleet_worker_id !== null)
        .length
    : undefined;

  // Every read the cards need: the flow always, a detail read when enabled.
  const needed = [
    { want: enabled, read: flowRead },
    { want: wantPreview, read: preview },
    { want: wantAssignment, read: assignment },
    { want: wantExceptions, read: exceptions },
    { want: wantEnRoute, read: enRoute },
  ].filter((n) => n.want);
  const failed = needed.some((n) => n.read.data === undefined && n.read.isError);
  const answered = needed.every((n) => n.read.data !== undefined);

  const flow = flowRead.data;
  const language = i18n.language;
  const held = useMemo(() => {
    const executor = flow ? flowProvider(flow) : null;
    const fmt: FocusFormat = {
      monthDay: (date) => formatMonthDay(date, language),
      batchDay: (date) => formatBatchDay(date, language),
      clock: (ms) =>
        new Intl.DateTimeFormat(language, {
          timeZone: flow?.timezone,
          hour: '2-digit',
          minute: '2-digit',
          hourCycle: 'h23',
        }).format(new Date(ms)),
      stage: (s) => t(STAGE_LABEL[s]),
    };
    return holdFocusCards(
      {
        today,
        now: new Date(nowMs),
        todayRow,
        nextBatch,
        gates: flow
          ? {
              packaging: flowGateTime(flow, 'packaging_cutoff'),
              dispatch: flowGateTime(flow, 'dispatch_cutoff'),
              windowEnd: flowGateTime(flow, 'delivery_window_end'),
            }
          : null,
        flowModel: flow ? flowStepModel(flow) : null,
        provider: executor === null ? null : t(EXECUTOR_LABEL[executor]),
        preview: previewData,
        exceptionList: exceptionData,
        assignment: assignmentData,
        enRouteDriverCount,
        eta,
      },
      fmt,
    );
  }, [
    today,
    nowMs,
    todayRow,
    nextBatch,
    flow,
    t,
    language,
    previewData,
    exceptionData,
    assignmentData,
    enRouteDriverCount,
    eta,
  ]);
  const cards = useMemo(() => capFocusCards(held), [held]);
  const allClear =
    cards.length === 0 && answered && !failed && !hasActionableToday(todayRow, nextBatch);
  return { cards, held, allClear, failed };
}
